import React from 'react';
import { RCLoadingBars } from '@reverbdotcom/cadence/components';

interface Props {
  name: string;
  formId: string;
  codeLength: number;
  initialValue?: string | null;
}

export function OneTimeCodeInput({
  name,
  formId,
  codeLength,
  initialValue = null,
}: Props) {
  const LABEL = 'Character';
  const seedValue = initialValue ?? '';
  const [values, setValues] = React.useState<string[]>(() => (
    Array.from({ length: codeLength }, (_, i) => seedValue[i] ?? '')
  ));
  const cellKeys = Array.from({ length: codeLength }, (_, i) => `cell-${i}`);
  const [submitted, setSubmitted] = React.useState(false);
  const cellRefs = React.useRef<Array<HTMLInputElement | null>>([]);

  const code = values.join('');

  // OTP UX: focus the first cell on mount.
  React.useEffect(() => {
    cellRefs.current[0]?.focus();
  }, []);

  React.useEffect(() => {
    if (submitted) return;
    if (code.length !== codeLength) return;
    const form = document.getElementById(formId);
    if (!(form instanceof HTMLFormElement)) return;
    setSubmitted(true);
    form.requestSubmit();
  }, [code, codeLength, formId, submitted]);

  function applyValues(nextValues: string[], focusIdx: number) {
    setValues(nextValues);
    cellRefs.current[focusIdx]?.focus();
  }

  function distribute(startIdx: number, chars: string[]) {
    const start = chars.length >= codeLength ? 0 : startIdx;
    const next = [...values];
    for (let i = 0; i < chars.length && start + i < codeLength; i += 1) {
      next[start + i] = chars[i];
    }
    applyValues(next, Math.min(start + chars.length, codeLength - 1));
  }

  function onInput(i: number, e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value.replace(/\s/g, '');
    if (v.length > 1) {
      distribute(i, v.split(''));
      return;
    }
    const next = [...values];
    next[i] = v;
    const focusIdx = v && i + 1 < codeLength ? i + 1 : i;
    applyValues(next, focusIdx);
  }

  function onPaste(i: number, e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\s/g, '');
    distribute(i, text.split(''));
  }

  function onKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    const goLeft = (e.key === 'Backspace' && !e.currentTarget.value) || e.key === 'ArrowLeft';
    if (goLeft && i > 0) {
      cellRefs.current[i - 1]?.focus();
    } else if (e.key === 'ArrowRight' && i + 1 < codeLength) {
      cellRefs.current[i + 1]?.focus();
    }
  }

  return (
    <>
      <input type="hidden" name={name} value={code} />

      <div className="one-time-code-cells scaling-mtb-4">
        {cellKeys.map((cellKey, i) => (
          <input
            key={cellKey}
            ref={(el) => { cellRefs.current[i] = el; }}
            className="one-time-code-cell"
            type="text"
            maxLength={1}
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            autoCapitalize="off"
            aria-label={`${LABEL} ${i + 1} of ${codeLength}`}
            disabled={submitted}
            value={values[i]}
            onChange={(e) => onInput(i, e)}
            onPaste={(e) => onPaste(i, e)}
            onKeyDown={(e) => onKeyDown(i, e)}
            onFocus={(e) => e.target.select()}
          />
        ))}
      </div>

      {submitted && (
        <div className="one-time-code-loading" role="status" aria-live="polite">
          <RCLoadingBars size="large" invertColor />
        </div>
      )}
    </>
  );
}
